import {
  IonButton,
  IonContent,
  IonInput,
  IonLoading,
  IonModal
} from '@ionic/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  ButtonContainer,
  ModalHeader,
  Padding,
  Spacer
} from '@/components/common';
import { Icon, Text } from '@/components/memo';
import { SelectModal } from '@/components/modals';
import { useDentist, useRequests } from '@/contexts';
import { Request } from '@/data/Requests';
import { Service, getDefaultServices, getServicesByDentistId } from '@/data/Services';
import { useModal } from '@/hooks';
import {
  PURPOSE_JOIN_SEPARATOR,
  SELECT_MODAL_OTHERS
} from '@/utils/constants';
import {
  buildPurposeString,
  parsePurposeForEdit
} from '@/utils/helpers/appointmentPurpose';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { arrowBackOutline, checkmarkCircleOutline } from 'ionicons/icons';

import '../calendar/manualBooking/ManualBooking.css';
import '../profile/updateServices/UpdateServices.css';

interface EditProceduresModalProps {
  isOpen: boolean;
  onClose: () => void;
  request: Request | null;
  presentingElement?: HTMLElement | undefined;
}

const EditProceduresModal: React.FC<EditProceduresModalProps> = ({
  isOpen,
  onClose,
  request,
  presentingElement
}) => {
  const { selectedDentist: dentist } = useDentist();
  const { updateRequestPurposeMutation } = useRequests();

  const [purpose, setPurpose] = useState<string | null>(null);
  const [othersPurpose, setOthersPurpose] = useState<string | null>(null);
  const [lastSavedPurpose, setLastSavedPurpose] = useState<string | null>(null);
  const [errors, setErrors] = useState<{
    purpose: boolean;
    othersPurpose: boolean;
  }>({ purpose: false, othersPurpose: false });

  const {
    isOpen: isPurposeSelectModalOpen,
    onOpen: onOpenPurposeSelectModal,
    onClose: onClosePurposeSelectModal
  } = useModal();

  const { data: dentistServices = [] }: UseQueryResult<Service[], Error> =
    useQuery({
      queryKey: ['services', dentist?.id],
      queryFn: () => getServicesByDentistId(dentist?.id!),
      enabled: isOpen && !!dentist?.id
    });

  const { data: defaultServices = [] }: UseQueryResult<Service[], Error> =
    useQuery({
      queryKey: ['defaultServices'],
      queryFn: getDefaultServices,
      enabled: isOpen
    });

  const purposeOptions: string[] = useMemo(() => {
    if (dentistServices.length > 0) {
      return dentistServices.map((service) => service.name);
    }
    return defaultServices.map((service) => service.name);
  }, [dentistServices, defaultServices]);

  const resetFromRequest = useCallback(() => {
    if (!request) {
      setPurpose(null);
      setOthersPurpose(null);
      setLastSavedPurpose(null);
      return;
    }
    const { segments, othersDetail } = parsePurposeForEdit(request.purpose);
    const joined = segments.join(PURPOSE_JOIN_SEPARATOR);
    setPurpose(joined || null);
    setOthersPurpose(othersDetail);
    setLastSavedPurpose(joined || null);
  }, [request]);

  useEffect(() => {
    if (isOpen && request) {
      resetFromRequest();
      setErrors({ purpose: false, othersPurpose: false });
    }
  }, [isOpen, request, resetFromRequest]);

  const isPurposeSelected = (value: string) =>
    purpose?.split(PURPOSE_JOIN_SEPARATOR).includes(value) || false;

  const isPurposePreSelected = (value: string) =>
    lastSavedPurpose?.split(PURPOSE_JOIN_SEPARATOR).includes(value) || false;

  const onSelectPurpose = (value: string, isSelected: boolean) => {
    if (purpose) {
      if (isSelected) {
        setPurpose(
          purpose
            .split(',')
            .map((item) => item.trim())
            .filter((item) => item !== value)
            .join(PURPOSE_JOIN_SEPARATOR)
        );
      } else {
        setPurpose(purpose + PURPOSE_JOIN_SEPARATOR + value);
      }
    } else {
      setPurpose(value);
    }
  };

  const onClosePurposeSelectModalWithSave = () => {
    onClosePurposeSelectModal();
    setLastSavedPurpose(purpose);
  };

  const onInputOthers = (value: string | null) => {
    setOthersPurpose(value);
    setErrors((prev) => ({ ...prev, othersPurpose: false }));
  };

  const handleDismiss = () => {
    resetFromRequest();
    onClose();
  };

  const submit = async () => {
    const segments = purpose
      ? purpose
          .split(PURPOSE_JOIN_SEPARATOR)
          .map((s) => s.trim())
          .filter(Boolean)
      : [];

    const needsOthersDetail = segments.includes(SELECT_MODAL_OTHERS);
    const newErrors = {
      purpose: segments.length === 0,
      othersPurpose: needsOthersDetail && !othersPurpose?.trim()
    };
    setErrors(newErrors);
    if (Object.values(newErrors).some(Boolean)) {
      return;
    }

    if (!request?.id) return;

    const built = buildPurposeString(segments, othersPurpose);
    await updateRequestPurposeMutation.mutateAsync({
      request,
      purpose: built || null
    });
    handleDismiss();
  };

  const copy = {
    header: (
      <>
        Update <Text color="primary">procedures</Text>
      </>
    ),
    message: 'Change the procedures for this appointment or request.',
    selectPurposeModalTitle: (
      <>
        Select <Text color="primary">procedures</Text>
      </>
    ),
    othersLabel: 'Others, please specify...',
    othersPlaceholder: 'Enter procedure (optional)',
    purposePlaceholder: 'Example: Braces Adjustment'
  };

  const loading = updateRequestPurposeMutation.isPending;

  return (
    <>
      <IonModal
        isOpen={isOpen && !!request}
        onDidDismiss={handleDismiss}
        presentingElement={presentingElement}
      >
        <IonContent>
          <Padding withSafeArea={!presentingElement}>
            <div className="update-services">
              <ModalHeader
                leftIcon={arrowBackOutline}
                onClickLeftIcon={handleDismiss}
              />
              <Spacer top={16} bottom={24}>
                <h1 className="update-services__header">{copy.header}</h1>
                <p className="update-services__message">{copy.message}</p>
              </Spacer>

              <Spacer bottom={124}>
                <div className="manual-booking__form">
                  <div
                    className={`manual-booking__input ${errors.purpose ? 'manual-booking__input--error' : ''}`}
                  >
                    <IonInput
                      id="edit-purpose-input"
                      label="Procedures"
                      labelPlacement="stacked"
                      value={purpose ?? ''}
                      placeholder={copy.purposePlaceholder}
                      onIonInput={(e: CustomEvent) => {
                        setPurpose(e.detail.value ?? null);
                        setErrors((prev) => ({ ...prev, purpose: false }));
                      }}
                      onClick={() => {
                        setErrors((prev) => ({ ...prev, purpose: false }));
                        onOpenPurposeSelectModal();
                      }}
                      readonly
                    />
                  </div>
                  {purpose?.split(PURPOSE_JOIN_SEPARATOR).includes(SELECT_MODAL_OTHERS) && (
                    <div
                      className={`manual-booking__input ${errors.othersPurpose ? 'manual-booking__input--error' : ''}`}
                    >
                      <IonInput
                        id="edit-others-input"
                        label={copy.othersLabel}
                        labelPlacement="stacked"
                        value={othersPurpose ?? ''}
                        placeholder={copy.othersPlaceholder}
                        onIonInput={(event: CustomEvent) =>
                          onInputOthers(event.detail.value ?? null)
                        }
                      />
                    </div>
                  )}
                </div>
              </Spacer>

              <ButtonContainer>
                <IonButton
                  id="save-procedures-button"
                  onClick={() => submit()}
                  expand="block"
                  fill="solid"
                  size="default"
                  color="primary"
                  strong={true}
                >
                  <Icon slot="start" icon={checkmarkCircleOutline} />
                  Update
                </IonButton>
              </ButtonContainer>
            </div>
          </Padding>
          <IonLoading isOpen={loading} message="Loading..." spinner="crescent" />
          <SelectModal
            isOpen={isPurposeSelectModalOpen}
            presentingElement={undefined}
            title={copy.selectPurposeModalTitle}
            options={purposeOptions}
            isSelected={isPurposeSelected}
            isPreSelected={isPurposePreSelected}
            onSelect={onSelectPurpose}
            onClose={onClosePurposeSelectModalWithSave}
            multiple={true}
          />
        </IonContent>
      </IonModal>
    </>
  );
};

export default EditProceduresModal;
